import { AriaAttributes, useCallback } from 'react';

import { useControlled, useInstanceId } from '@adsk/alloy-react-helpers';

import { useTranslation } from '../i18n';

export default <TKey extends string | number>({
  defaultValue,
  value,
  onChange,
}: {
  defaultValue: TKey[];
  value: TKey[] | undefined;
  onChange: ((arr: TKey[]) => void) | undefined;
}) => {
  // Translation is used for ARIA, not the UI, so we can use it without suspense
  const { t: getUnsuspendedTranslation } = useTranslation('translation', {
    useSuspense: false,
  });

  const [selectedValue, setSelectedValue] = useControlled<TKey[]>(
    defaultValue,
    value,
    onChange,
  );

  const menuId = `alloy-menu-${useInstanceId()}`;

  const getControlProps = useCallback<
    (args: { show: boolean | undefined }) => AriaAttributes
  >(
    ({ show }) => ({
      'aria-haspopup': 'menu',
      'aria-expanded': show,
      'aria-label': getUnsuspendedTranslation('TOGGLE_MENU'),
      'aria-controls': menuId,
    }),
    [menuId, getUnsuspendedTranslation],
  );

  const getMenuProps = useCallback(
    () => ({
      id: menuId,
      value: selectedValue,
      onChange: setSelectedValue,
    }),
    [menuId, selectedValue, setSelectedValue],
  );

  return {
    selectedValue,
    getControlProps,
    getMenuProps,
  };
};
